import React, { useState, useEffect } from 'react';
import Typography from '../components/Text/Typography';
import TextInput from '../components/Text/TextInput';
import Button from '../components/Button/Button';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import './Contact.css';

const Contact: React.FC = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Prefill name and email for logged-in users, but don't overwrite user edits
  useEffect(() => {
    if (!user) return;
    setFormData(prev => ({
      ...prev,
      name: prev.name || user.name || '',
      email: prev.email || user.email || ''
    }));
  }, [user]);

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setSubmitStatus('error');
        showToast(data.error || 'Failed to send message. Please try again or email us directly.', 'error');
        return;
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      showToast('Message sent! I\'ll get back to you soon.');
    } catch (err) {
      setSubmitStatus('error');
      showToast('Something went wrong. Please try again or email dew.don.lee@gmail.com directly.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = formData.name.trim() && formData.email.trim() && formData.message.trim();

  return (
    <div className="contact-page">
      <div className="contact-container">
        <Typography variant="h1" className="contact-title">
          Let's Connect
        </Typography>
        
        <div className="contact-content">
          <div className="contact-intro">
            <Typography variant="body" className="intro-text">
              I'd love to hear from you! Whether you have feedback about Dew, questions about skincare, 
              or just want to share your experience, I'm genuinely excited to connect with you.
            </Typography>
            
            <Typography variant="body" className="intro-text">
              Your thoughts help shape how Dew grows and improves. Every message you send helps me 
              understand what matters most to you and how I can make Dew even better for your skincare journey.
            </Typography>
          </div>

          <div className="contact-form-section">
            <Typography variant="h2" className="form-title">
              Send me a message
            </Typography>
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-field">
                  <TextInput
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    placeholder="Your name"
                    label="Name"
                  />
                </div>
                <div className="form-field">
                  <TextInput
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="your.email@example.com"
                    label="Email"
                    type="email"
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label className="textarea-label">
                  <Typography variant="body-small-bold">Message</Typography>
                </label>
                <textarea
                  value={formData.message}
                  onChange={handleInputChange('message')}
                  placeholder="Tell me what's on your mind..."
                  className="message-textarea"
                  rows={6}
                />
              </div>

              {submitStatus === 'success' && (
                <div className="success-message">
                  <Typography variant="body" className="success-text">
                    Thank you! Your message has been sent. I'll get back to you soon.
                  </Typography>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="error-message">
                  <Typography variant="body" className="error-text">
                    Something went wrong. Please try again or email me directly.
                  </Typography>
                </div>
              )}

              <Button
                variant="primary"
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className="submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>

          <div className="direct-contact-section">
            <Typography variant="h3" className="direct-contact-title">
              Or email me directly
            </Typography>
            
            <div className="email-container">
              <a 
                href="mailto:dew.don.lee@gmail.com" 
                className="email-link"
              >
                <Typography variant="body-bold" className="email-text">
                  dew.don.lee@gmail.com
                </Typography>
              </a>
            </div>
            
            <Typography variant="body" className="direct-contact-text">
              Feel free to reach out anytime. I typically respond within 24 hours, 
              and I read every message personally.
            </Typography>
          </div>

          <div className="contact-closing">
            <Typography variant="body" className="closing-text">
              Thank you for being part of the Dew community. I'm looking forward to hearing from you!
            </Typography>
            
            <div className="closing-signature">
              <Typography variant="body" style={{ fontStyle: 'italic' }}>
                With gratitude,
              </Typography>
              <Typography variant="body" className="signature-name">
                Don
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
